<template>
    <Clickable
        :href="resource.slug ? `/resource/${resource.slug}` : null"
        :class="[viewport.is, { featured, portrait }]"
        class="resource-card"
    >
        <div class="resource-card-body">
            <Chip
                v-if="resource.topic"
                :style="{
                    '--chip-background-colour': `rgba(${topicColourRgb}, 0.1)`,
                    '--chip-colour': `rgb(${topicColourRgb})`,
                }"
                :text="{ size: 'xxxs', heading: true }"
            >
                {{ resource.topic.value && resource.topic.value.data.title }}
            </Chip>
            <Txt :size="featured ? (viewport.is.xl ? 'xl' : viewport.is.mobile ? 'm' : 'l') : 'm'" heading>
                {{ resource.title }}
            </Txt>
            <Txt
                :size="featured ? 'l' : 'm'"
                :clamp="4"
                :value="resource.description"
            />
            <div class="resource-card-footer">
                <ClientOnly>
                    <Txt size="s" class="anchor">
                        {{ $date(resource.postDate).format('ll') }}
                    </Txt>
                </ClientOnly>
                <Txt v-if="resource.contentType" size="s" class="anchor">
                    {{ resource.contentType.value && resource.contentType.value.data.title }}
                </Txt>
            </div>
        </div>
        <div class="resource-image">
            <NuxtImg
                v-if="resource.image"
                :src="resource.image"
                :alt="resource.title"
                provider="builderio"
                width="492"
                height="478"
                sizes="xl:30vw lg:30vw md:30vw sm:30vw xs:30vw"
                loading="lazy"
            />
        </div>
    </Clickable>
</template>

<script>
import { useResponsive } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Chip from '@/components/utility/chip';
import Clickable from '@/components/utility/clickable';

export default {
    name: 'ResourceCard',
    components: { Txt, Chip, Clickable },
    inheritAttrs: false,
    props: {
        resource: { type: Object, required: true },

        portrait: { type: Boolean, default: false },
        featured: { type: Boolean, default: false }
    },
    data() {
        return {
            viewport: { is: {} }
        };
    },
    computed: {
        topicColourRgb() {
            if (!(this.resource.topic && this.resource.topic.value && this.resource.topic.value.data.colour)) return;
            return this.resource.topic.value.data.colour
                .replace('rgba(', '')
                .split(',')
                .slice(0, 3)
                .join(',');
        }
    },
    mounted() {
        this.viewport = useResponsive();
    }
};
</script>

<style lang="less" scoped>
.resource-card {
    display: flex;
    overflow: hidden;
    border-radius: 24px;
    background-color: #FFF;
    color: var(--coal);
    box-shadow: var(--shadow-base);
    transition: var(--smooth);
    width: 100%;

    &:hover {
        box-shadow: var(--shadow-xl);
    }

    .resource-image {
        display: flex;
        overflow: hidden;
        flex: 0 0 297px;
        width: 297px;
        aspect-ratio: 492 / 478;
        pointer-events: none;
        img {
            width: 100%;
            object-fit: cover;
        }
    }
    .resource-card-body {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 24px;
        gap: 8px;
        .chip { margin-bottom: 8px; }
        > .txt-component.body { flex: 1; }
        .resource-card-footer {
            display: flex;
            justify-content: space-between;
            border-top: 1px solid var(--smoke);
            padding-top: 16px;
            width: 100%;
        }
    }
    &.featured {
        flex-direction: column-reverse;
        .resource-card-body {
            gap: 16px;
            padding: 32px;
        }
        .resource-image {
            position: relative;
            width: 100%;
            height: 100%;
            flex: 0 0 100%;
            aspect-ratio: 772 / 380;
            &::after {
                content: "";
                position: absolute;
                left: -100%;
                top: -100%;
                width: 230%;
                height: 190%;
                transform: rotate(100deg);
                background: radial-gradient(50% 50% at 50% 50%, var(--primary) 0%, rgba(255, 255, 255, 0) 100%);
            }
        }
    }
    &.xl {
        &.featured {
            flex-direction: row-reverse;
            .resource-image {
                width: 492px;
                flex: 0 0 492px;
                aspect-ratio: 492 / 478;
            }
        }
    }
    &.mobile, &.portrait {
        flex-direction: column-reverse;
        .resource-image {
            width: 100%;
            flex: 0 0 100%;
            aspect-ratio: 327 / 213;
            img { height: 100%; }
        }
    }
    &.portrait {
        .resource-image { flex: 0 0 auto; }
    }
}
</style>
